/**
 * Excel Export Utility
 * Creates proper Excel (.xlsx) files with formatting and auto-sizing
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import * as XLSX from "xlsx";
// Helper function to calculate optimal column width
var calculateColumnWidth = function (data, key, header) {
    if (!data || data.length === 0)
        return 15;
    // Get the maximum length of content in this column
    var maxLength = header.length;
    for (var _i = 0, _a = data.slice(0, 100); _i < _a.length; _i++) {
        var row = _a[_i];
        // Sample first 100 rows for performance
        var value = row[key];
        if (value !== null && value !== undefined) {
            var stringValue = String(value);
            maxLength = Math.max(maxLength, stringValue.length);
        }
    }
    // Add some padding and cap the width
    var width = Math.min(Math.max(maxLength + 2, 10), 50);
    return width;
};
// Helper function to format cell values
var formatCellValue = function (value) {
    if (value === null || value === undefined)
        return "";
    // Format dates
    if (typeof value === "string" && value.includes("T") && value.includes("Z")) {
        try {
            var date = new Date(value);
            if (!isNaN(date.getTime())) {
                return date.toLocaleString();
            }
        }
        catch (_a) {
            // If date parsing fails, return original value
        }
    }
    // Format booleans
    if (typeof value === "boolean") {
        return value ? "Yes" : "No";
    }
    // Format numbers with proper locale
    if (typeof value === "number") {
        return value.toLocaleString();
    }
    return String(value);
};
/**
 * Enhanced Excel export using the xlsx library
 * Creates proper .xlsx files with formatting and auto-sizing
 */
export var exportToExcelWithXLSX = function (_a) { return __awaiter(void 0, [_a], void 0, function (_b) {
    var workbook, processedData, finalHeaders, columnWidths, visibleFields_1, sampleRow, allKeys_1, worksheet, range, col, cellAddress, timestamp, finalFilename, workbookOutput, blob, url, link;
    var filename = _b.filename, data = _b.data, _c = _b.sheetName, sheetName = _c === void 0 ? "Sheet1" : _c, headers = _b.headers, fieldConfig = _b.fieldConfig;
    return __generator(this, function (_d) {
        if (!data || data.length === 0) {
            throw new Error("No data provided for export");
        }
        workbook = XLSX.utils.book_new();
        processedData = data;
        finalHeaders = headers;
        columnWidths = [];
        // If field configuration is provided, use it to filter and order columns
        if (fieldConfig && fieldConfig.length > 0) {
            visibleFields_1 = fieldConfig.filter(function (field) { return field.visible; }).sort(function (a, b) { return a.order - b.order; });
            if (visibleFields_1.length > 0) {
                // Create headers from field configuration
                finalHeaders = visibleFields_1.map(function (field) { return field.label; });
                // Filter and reorder data based on field configuration
                processedData = data.map(function (row) {
                    var newRow = {};
                    visibleFields_1.forEach(function (field) {
                        newRow[field.label] = formatCellValue(row[field.key]);
                    });
                    return newRow;
                });
                // Calculate column widths for visible fields
                columnWidths = visibleFields_1.map(function (field) { return ({
                    wch: calculateColumnWidth(data, field.key, field.label),
                }); });
            }
        }
        else {
            sampleRow = data[0];
            allKeys_1 = Object.keys(sampleRow);
            if (!finalHeaders) {
                finalHeaders = allKeys_1;
            }
            // Format all data
            processedData = data.map(function (row) {
                var newRow = {};
                allKeys_1.forEach(function (key) {
                    newRow[key] = formatCellValue(row[key]);
                });
                return newRow;
            });
            // Calculate column widths for all fields
            columnWidths = allKeys_1.map(function (key) { return ({
                wch: calculateColumnWidth(data, key, (finalHeaders === null || finalHeaders === void 0 ? void 0 : finalHeaders.find(function (h) { return h === key; })) || key),
            }); });
        }
        worksheet = finalHeaders
            ? XLSX.utils.json_to_sheet(processedData, { header: finalHeaders })
            : XLSX.utils.json_to_sheet(processedData);
        // Set column widths
        if (columnWidths.length > 0) {
            worksheet["!cols"] = columnWidths;
        }
        range = XLSX.utils.decode_range(worksheet["!ref"] || "A1");
        for (col = range.s.c; col <= range.e.c; col++) {
            cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
            if (worksheet[cellAddress]) {
                worksheet[cellAddress].s = {
                    font: { bold: true },
                    fill: { fgColor: { rgb: "E8F4FD" } },
                    alignment: { horizontal: "center" },
                };
            }
        }
        // Add the worksheet to the workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
        timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, "-");
        finalFilename = "".concat(filename, "_").concat(timestamp, ".xlsx");
        workbookOutput = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array",
        });
        blob = new Blob([workbookOutput], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        url = URL.createObjectURL(blob);
        link = document.createElement("a");
        link.href = url;
        link.download = finalFilename;
        link.style.display = "none";
        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // Clean up
        URL.revokeObjectURL(url);
        return [2 /*return*/];
    });
}); };
/**
 * Legacy CSV export function (kept for backwards compatibility)
 */
export var exportToExcel = function (_a) { return __awaiter(void 0, [_a], void 0, function (_b) {
    var finalHeaders, csvContent, blob, url, link, timestamp;
    var filename = _b.filename, data = _b.data, _c = _b.sheetName, _sheetName = _c === void 0 ? "Sheet1" : _c, headers = _b.headers;
    return __generator(this, function (_d) {
        if (!data || data.length === 0) {
            throw new Error("No data provided for export");
        }
        finalHeaders = headers || Object.keys(data[0]);
        csvContent = __spreadArray([
            // Header row
            finalHeaders.join(",")
        ], data.map(function (row) {
            return finalHeaders
                .map(function (header) {
                var value = row[header];
                // Handle null/undefined values
                if (value == null)
                    return "";
                // Escape commas and quotes in values
                var stringValue = String(value);
                if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
                    return "\"".concat(stringValue.replace(/"/g, '""'), "\"");
                }
                return stringValue;
            })
                .join(",");
        }), true).join("\n");
        blob = new Blob([csvContent], {
            type: "text/csv;charset=utf-8",
        });
        url = URL.createObjectURL(blob);
        link = document.createElement("a");
        link.href = url;
        timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, "-");
        link.download = "".concat(filename, "_").concat(timestamp, ".csv");
        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // Clean up
        URL.revokeObjectURL(url);
        return [2 /*return*/];
    });
}); };
/**
 * Utility function to flatten nested objects for Excel export
 */
export var flattenObjectForExport = function (obj, prefix) {
    if (prefix === void 0) { prefix = ""; }
    var flattened = {};
    Object.keys(obj).forEach(function (key) {
        var value = obj[key];
        var newKey = prefix ? "".concat(prefix, ".").concat(key) : key;
        if (value != null && typeof value === "object" && !Array.isArray(value)) {
            // Recursively flatten nested objects
            Object.assign(flattened, flattenObjectForExport(value, newKey));
        }
        else if (Array.isArray(value)) {
            // Convert arrays to comma-separated strings
            flattened[newKey] = value.join(", ");
        }
        else {
            flattened[newKey] = value;
        }
    });
    return flattened;
};
/**
 * Utility to prepare data for export by flattening and cleaning
 */
export var prepareDataForExport = function (data, options) {
    if (options === void 0) { options = {}; }
    var _a = options.flatten, flatten = _a === void 0 ? false : _a, _b = options.customMapping, customMapping = _b === void 0 ? {} : _b;
    return data.map(function (item) {
        var processedItem = flatten ? flattenObjectForExport(item) : __assign({}, item);
        // Apply custom field mapping if provided
        if (Object.keys(customMapping).length > 0) {
            var mappedItem_1 = {};
            Object.keys(processedItem).forEach(function (key) {
                var mappedKey = customMapping[key] || key;
                mappedItem_1[mappedKey] = processedItem[key];
            });
            processedItem = mappedItem_1;
        }
        return processedItem;
    });
};
// Default export for the main export function
export default exportToExcelWithXLSX;
